
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { cloneDeep } from 'lodash';
import { OPTION } from '@/utils/constants';
import { PERMISSION } from '@/permissions';
import { formatValue } from '@/utils/format';
import { getDefaultTaxesApplied } from '@/utils/getters';
import draggable from 'vuedraggable';
import IconButton from '@/components/shared/suites/IconButton.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TaxRateSelect from '@/components/shared/TaxRateSelect.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'auth';

@Component<AddonEditor>({
	components: {
		draggable,
		IconButton,
		SelectComponent,
		TaxRateSelect,
		TextField
	}
})
export default class AddonEditor extends Vue {
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('showReportingCategory', { namespace }) private showReportingCategory!: boolean;
	@Getter('isPosIntegrated', { namespace }) private isPosIntegrated!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getPriceAffixes', { namespace }) private priceAffixes!: PriceAffixes;
	@Prop({ type: Array, required: true }) private addons!: Option[];
	@Prop({ type: Boolean, default: false }) private disabled!: boolean;
	@Prop({ type: Boolean, default: false }) private customQuantities!: boolean;

	private PERMISSION = PERMISSION;
	private allowCustomQuantities = this.customQuantities;
	private allowTaxOverride = false;
	private mutableAddOns: Option[] = [];
	private showExtras: boolean[] = [];

	private get defaultTaxesApplied(): string {
		return getDefaultTaxesApplied(this.restaurant);
	}

	private created(): void {
		this.allowTaxOverride = this.addons.some((option: Option) => option.tax_rate && Object.keys(option.tax_rate).length);
		this.mutableAddOns = cloneDeep(this.addons);
		if (this.disabled) {
			this.mutableAddOns = this.mutableAddOns.filter((item: Option) => (item.price));
		}
		this.showExtras = this.mutableAddOns.map(() => false);
	}

	private formatPricingItemPrice = (price: any) => formatValue(price, '', 'price');

	private formatPricingItemCalories = (calories: number) => formatValue(calories, null, 'calories');

	/**
	 * Toggle the extras view (description + calories) for a given option row
	 *
	 * @param {number} index
	 * @return {void}
	 */
	private toggleExtras(index: number): void {
		Vue.set(this.showExtras, index, !this.showExtras[index]);
	}

	/**
	 * Add a new add-on and emit updated list
	 *
	 * @return {void}
	 */
	private addAddOn (): void {
		this.mutableAddOns.push({ ...OPTION, order: this.mutableAddOns.length });
		this.showExtras.push(false);
		this.$emit('addons-updated', this.mutableAddOns);
	}

	/**
	 * Remove an add-on form and emit updated list
	 *
	 * @param {number} index
	 * @return {void}
	 */
	private removeAddOn (index: number): void {
		const existingAddonId = this.mutableAddOns[index]?.id;
		this.mutableAddOns.splice(index, 1);
		this.showExtras.splice(index, 1);

		// Adjust orders for items that were below the removed addon
		for (let i = index; i < this.mutableAddOns.length; i++) {
			this.mutableAddOns[i].order = i;
		}

		if (existingAddonId) {
			this.$emit('addon-removed', { removedAddonId: existingAddonId, updatedAddons: this.mutableAddOns });
		}
		else {
			this.$emit('addons-updated', this.mutableAddOns);
		}
	}

	/**
	 * Update custom quantities flag and reset max quantities if disabled
	 *
	 * @return {void}
	 */
	private updateCustomQuantities(): void {
		if(!this.allowCustomQuantities) {
			this.addons.forEach((item: Option) => {
				item.max_quantity = 1;
			});
		}

		this.$emit('update-custom-quantities', this.allowCustomQuantities);
	}

	/**
	 * Update the order of addons after drag and drop
	 *
	 * @param {any} evt
	 * @return {Promise<void>}
	 */
	private async updateAddonsOrder(evt: any): Promise<void> {
		const { newIndex, oldIndex } = evt;

		if (newIndex !== oldIndex) {
			// Mirror the row reorder in the per-row extras state
			const movedExtras = this.showExtras.splice(oldIndex, 1)[0];
			this.showExtras.splice(newIndex, 0, movedExtras);

			this.mutableAddOns[newIndex].order = newIndex;

			// Adjust order of items between old and new index
			const startIndex = Math.min(newIndex, oldIndex) + (newIndex < oldIndex ? 1 : 0); // If order is moving up, start from the next item
			const endIndex = Math.max(newIndex, oldIndex);

			for (let i = startIndex; i <= endIndex; i++) {
				this.mutableAddOns[i].order = i;
			}

			this.$emit('addons-updated', this.mutableAddOns);
		}
	}

	/**
	 * Remove all tax rates if tax override is disabled
	 *
	 * @return {void}
	 */
	private updateTaxRates(): void {
		if (!this.allowTaxOverride) {
			this.mutableAddOns.forEach((item: Option) => {
				Vue.set(item, 'tax_rate', null);
			});
			this.$emit('addons-updated', this.mutableAddOns);
		}
	}

	/**
	 * Handle tax rate selection for an add-on
	 *
	 * @param {Option} item
	 * @param {Taxes} selectedTaxRates
	 * @return {void}
	 */
	private handleTaxRateSelection(item: Option, selectedTaxRates: Taxes): void {
		Vue.set(item, 'tax_rate', selectedTaxRates);
		this.$emit('addons-updated', this.mutableAddOns);
	}
}
